import { PointAddResponse } from "@1games-ru/region-shared/dist/client/requests/point";
import { HouseLevel } from "@1games-ru/region-shared/dist/enums/estate/house";
import { faker } from "@faker-js/faker";
import { useCallback, useEffect, useState } from "react";
import { Button, Select, SelectOption, TextInput } from "@/shared/ui";
import { Point } from "@/domains/createEntity/constants/point";
import getInitPoint from "@/domains/createEntity/helpers/getInitPoint";
import toast from "@/domains/toasts/helpers/toast";
import useCreateHouse from "@/domains/createEntity/hooks/useCreateHouse";
import usePoint from "@/domains/createEntity/hooks/usePoint";
import CreateEntityRow from "@/domains/createEntity/components/CreateEntityRow";
import CreateEntityScroll from "@/domains/createEntity/components/CreateEntityScroll";
import CreateEntitySetLocation from "@/domains/createEntity/components/CreateEntitySetLocation";
import CreateEntityTitle from "@/domains/createEntity/components/CreateEntityTitle";

type Props = {
  onClose?: () => void;
};

export default function CrateEntityHouseFormContainer({ onClose }: Props) {
  const { addPoint, clearPoints } = usePoint();
  const { createHouse } = useCreateHouse();
  const [level, setLevel] = useState<HouseLevel>(HouseLevel.Economy);
  const [houseInterior, setHouseInterior] = useState("");
  const [housePoint, setHousePoint] = useState<PointAddResponse | null>(null);
  const [garageInterior, setGarageInterior] = useState("");
  const [garagePoint, setGaragePoint] = useState<PointAddResponse | null>(null);
  const [price, setPrice] = useState("");

  const [interiorPoint, setInteriorPoint] = useState<PointAddResponse | null>(null);

  const clear = useCallback(async () => {
    const response = await clearPoints();
    if (response) {
      setHousePoint(null);
      setGaragePoint(null);
      setLevel(HouseLevel.Economy);
      setHouseInterior("");
      setGarageInterior("");
      setPrice("");
    }
  }, []);

  useEffect(() => {
    return () => {
      clear();
    };
  }, [clear]);

  const createHandler = useCallback(async () => {
    if (!garageInterior || !garagePoint || !houseInterior || !housePoint || !price) {
      toast.error("Заполните все поля");
      return;
    }
    const response = await createHouse({
      level,
      interiorId: Number(houseInterior),
      garageId: Number(garageInterior),
      entrance: {
        position: housePoint.position,
        heading: housePoint.heading,
      },
      garage: {
        position: garagePoint.position,
        heading: garagePoint.heading,
      },
      price: Number(price),
      streetName: faker.location.street(),
      dimension: housePoint.dimension,
    });
    if (response) {
      clear();
    }
  }, [clear, createHouse, garageInterior, garagePoint, houseInterior, housePoint, level, price]);

  return (
    <>
      <CreateEntityTitle title="Дом" onClose={onClose} />
      <CreateEntityScroll>
        <div className="flex flex-col gap-3.5">
          <CreateEntityRow label="Уровень дома">
            <Select className="z-3" value={level}>
              {[
                HouseLevel.Economy,
                HouseLevel.Standard,
                HouseLevel.Business,
                HouseLevel.Luxury,
              ].map((item) => (
                <SelectOption key={item} onClick={() => setLevel(item)}>
                  {item}
                </SelectOption>
              ))}
            </Select>
          </CreateEntityRow>

          {/* Интерьер дома */}
          <CreateEntityRow label="Интерьер дома">
            <TextInput
              value={houseInterior}
              onChange={setHouseInterior}
              placeholder="Интерьер дома"
              type="number"
              icon="🏠"
            />
          </CreateEntityRow>

          <CreateEntityRow label="Местоположение интерьера">
            <CreateEntitySetLocation
              is={!!interiorPoint}
              onChange={async () => {
                setInteriorPoint(await addPoint(getInitPoint(Point.HouseLocation)));
              }}
            />
          </CreateEntityRow>

          {/* Other */}
          <CreateEntityRow label="Местоположение дома">
            <CreateEntitySetLocation
              is={!!housePoint}
              onChange={async () => {
                setHousePoint(await addPoint(getInitPoint(Point.HouseLocation)));
              }}
            />
          </CreateEntityRow>
          <CreateEntityRow label="Интерьер гаража">
            <TextInput
              value={garageInterior}
              onChange={setGarageInterior}
              placeholder="Интерьер гаража"
              type="number"
            />
          </CreateEntityRow>
          <CreateEntityRow label="Местоположение гаража">
            <CreateEntitySetLocation
              is={!!garagePoint}
              onChange={async () => {
                setGaragePoint(await addPoint(getInitPoint(Point.GarageLocation)));
              }}
            />
          </CreateEntityRow>
          <CreateEntityRow label="Цена дома">
            <TextInput
              value={price}
              onChange={setPrice}
              placeholder="Введите сумму"
              type="number"
              icon="₽"
            />
          </CreateEntityRow>
        </div>
      </CreateEntityScroll>
      <Button onClick={createHandler}>Сохранить дом</Button>
    </>
  );
}
